import { useState } from 'react';
import PropTypes from 'prop-types';
import EmojiPickerReact, { EmojiStyle, Categories } from 'emoji-picker-react';
import {
    FloatingFocusManager,
    autoUpdate,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
} from '@floating-ui/react';
import { flip, offset } from '@floating-ui/react-dom';
import { useTranslation } from 'react-i18next';
import { cn } from 'Utils/cn';

export default function EmojiPicker(props) {
    const { value, onChange, className } = props;

    const [isOpen, setIsOpen] = useState(false);

    const { t } = useTranslation('common');

    const { x, y, refs, strategy, context } = useFloating({
        whileElementsMounted: autoUpdate,
        open: isOpen,
        placement: 'bottom-center',
        middleware: [flip(), offset(8)],
        onOpenChange: setIsOpen,
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([
        useClick(context),
        useRole(context, { role: 'menu' }),
        useDismiss(context),
    ]);

    const handleSelectEmoji = (ev) => {
        onChange(ev.emoji);
        setIsOpen(false);
    };

    return (
        <>
            <button
                ref={refs.setReference}
                {...getReferenceProps({
                    type: 'button',
                    className: cn('size-22 flex items-center justify-center', className),
                })}
            >
                <span className="text-3xl leading-none">{value}</span>
            </button>

            {isOpen && (
                <FloatingFocusManager context={context}>
                    <div
                        {...getFloatingProps({
                            ref: refs.setFloating,
                            className: 'z-dropdown shadow-sm rounded-lg',
                            style: {
                                position: strategy,
                                top: y ?? 0,
                                left: x ?? 0,
                            },
                        })}
                    >
                        <EmojiPickerReact
                            onEmojiClick={handleSelectEmoji}
                            lazyLoadEmojis
                            searchPlaceHolder={t('search-placeholder')}
                            previewConfig={{
                                showPreview: false,
                            }}
                            height={380}
                            emojiStyle={EmojiStyle.NATIVE}
                            categories={[
                                {
                                    name: t('emoji-categories.smiles-people'),
                                    category: Categories.SMILEYS_PEOPLE,
                                },
                                {
                                    name: t('emoji-categories.animals-nature'),
                                    category: Categories.ANIMALS_NATURE,
                                },
                                {
                                    name: t('emoji-categories.food-drink'),
                                    category: Categories.FOOD_DRINK,
                                },
                                {
                                    name: t('emoji-categories.travel-places'),
                                    category: Categories.TRAVEL_PLACES,
                                },
                                {
                                    name: t('emoji-categories.activities'),
                                    category: Categories.ACTIVITIES,
                                },
                                {
                                    name: t('emoji-categories.objects'),
                                    category: Categories.OBJECTS,
                                },
                                {
                                    name: t('emoji-categories.symbols'),
                                    category: Categories.SYMBOLS,
                                },
                                {
                                    name: t('emoji-categories.flags'),
                                    category: Categories.FLAGS,
                                },
                            ]}
                        />
                    </div>
                </FloatingFocusManager>
            )}
        </>
    );
}

EmojiPicker.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    className: PropTypes.string,
};
